import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Markdown } from '@/shared/components/markdown/Markdown';
import { getErrorStatus } from '@/core/api/response-handler';
import { ButonDescarcaExercitii } from '@/domains/lessons/components/ButonDescarcaExercitii';
import { ButonDescarcaRaspunsuri } from '@/domains/lessons/components/ButonDescarcaRaspunsuri';
import { infoSlidesForDetails, termenExpirat, ziSiLuna } from '@/domains/lessons/features/practical/practical-evidence';
import { collabApi } from '../api/collab.api';
import { configTip, type TipColab } from '../utils/tipuri-colab';
import { EtichetaTip } from './EtichetaTip';
import { VideoIncorporat } from './VideoIncorporat';

interface Props {
    token: string;
    lessonId: string;
    tip: TipColab;
    onContinue: () => void;
    onBack: () => void;
}

/**
 * Pasul 2: detaliile activitatii alese - ce vede si profesorul pe pagina lui „Detalii", plus filmul
 * si fisele exercitiului cu clasa. Pana pe 2026-10-05 colaboratorul vedea doar titlul si doua randuri
 * de descriere, deci nu avea de unde sti ce are de facut.
 *
 * Un 404 AICI nu inseamna „link invalid" (linkul tocmai a incarcat lista), ci o activitate ascunsa
 * sau scoasa intre timp: omul e trimis inapoi la lista, nu la coordonator.
 */
export function StepActivityDetails({ token, lessonId, tip, onContinue, onBack }: Props) {
    const cfg = configTip(tip);
    const { data: lesson, isLoading, isError, error, refetch, isFetching } = useQuery({
        queryKey: ['collab', token, 'activity', lessonId],
        queryFn: () => collabApi.getActivity(token, lessonId),
        retry: false,
    });
    const sectiuni = useMemo(() => infoSlidesForDetails(lesson?.slides), [lesson?.slides]);
    const filme = useMemo(
        () => (lesson?.slides ?? []).filter((s) => s.type === 'VIDEO_PREVIEW' && s.video),
        [lesson?.slides]
    );

    if (isLoading) {
        return (
            <div className="space-y-4" aria-busy="true" aria-label="Se încarcă activitatea">
                <Skeleton className="h-6 w-40" />
                <Skeleton className="h-8 w-3/4" />
                <Skeleton className="aspect-video w-full rounded-lg" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
            </div>
        );
    }
    if (isError || !lesson) {
        const disparuta = getErrorStatus(error) === 404;
        return (
            <div role="alert" className="flex flex-col gap-4 pt-2" data-colab-detalii-eroare={disparuta ? '404' : 'retea'}>
                <p className="text-fluid-base text-pretty">
                    {disparuta
                        ? 'Această activitate nu mai este disponibilă. Alege alta din listă.'
                        : 'Nu am putut încărca activitatea. Verifică conexiunea și încearcă din nou.'}
                </p>
                {!disparuta && (
                    <Button type="button" className="min-h-12 w-full" onClick={() => refetch()} disabled={isFetching}>
                        <RefreshCw className="size-4" aria-hidden /> Încearcă din nou
                    </Button>
                )}
                <Button type="button" variant="outline" className="min-h-12 w-full" onClick={onBack}>Înapoi la listă</Button>
            </div>
        );
    }

    const titlu = lesson.title ?? '';
    const expirat = termenExpirat(lesson.deadline);
    const zi = ziSiLuna(lesson.deadline);
    const areFisa = !!lesson.pdfManualUrl;
    const areRaspunsuri = !!lesson.areRaspunsuriPdf;

    return (
        <>
            <article className="space-y-6 text-base leading-relaxed" data-colab-detalii={tip}>
                <header className="space-y-2">
                    <EtichetaTip tip={tip} />
                    <h2 className="text-fluid-2xl font-bold leading-tight text-balance">{titlu}</h2>
                    {zi && (
                        <p role={expirat ? 'status' : undefined} className={expirat ? 'font-medium text-warning' : 'text-sm text-dark/70'}>
                            {expirat ? 'Termenul acestei activități a expirat. Nu se mai pot trimite dovezi.' : `Se pot trimite dovezi până pe ${zi}.`}
                        </p>
                    )}
                </header>

                {/* Filmul sta sus, in locul copertei: primul pas e „Urmărește filmul", iar coperta
                    exercitiilor e un cadru din el. Titlul e fix - textul slide-ului e un nume intern
                    („Lectia DEEE (1)"), nu ceva de aratat profesorului. */}
                {filme.length > 0
                    ? filme.map((f, i) => (
                        <section key={f.id ?? `film-${i}`} className="space-y-2">
                            <h3 className="text-fluid-lg font-semibold">{filme.length > 1 ? `Filmul lecției (${i + 1})` : 'Filmul lecției'}</h3>
                            <VideoIncorporat video={f.video} start={f.videoStart} end={f.videoEnd} titlu={`Filmul lecției: ${titlu}`} />
                        </section>
                    ))
                    : lesson.image && (
                        <img
                            src={lesson.image}
                            alt=""
                            width={1200}
                            height={675}
                            className="aspect-video w-full rounded-lg object-cover"
                        />
                    )}

                <section aria-labelledby="colab-pasi" className="rounded-lg border border-hairline bg-white p-4">
                    <h3 id="colab-pasi" className="font-semibold">Ce ai de făcut</h3>
                    <ol className="mt-2 list-decimal space-y-1 pl-5 text-pretty">
                        {cfg.pasi.map((pas) => <li key={pas}>{pas}</li>)}
                    </ol>
                </section>

                <Markdown source={lesson.description} />

                {sectiuni.map((s) => (
                    <section key={s.key} className="space-y-3">
                        {s.title && <h3 className="text-fluid-lg font-semibold text-balance">{s.title}</h3>}
                        {s.image && (
                            <img
                                src={s.image}
                                alt=""
                                width={1200}
                                height={675}
                                loading="lazy"
                                className="aspect-video w-full rounded-lg object-contain"
                            />
                        )}
                        <Markdown source={s.content} />
                    </section>
                ))}

                {(areFisa || areRaspunsuri) && (
                    <section aria-labelledby="colab-materiale" className="space-y-3" data-colab-materiale>
                        <h3 id="colab-materiale" className="text-fluid-lg font-semibold">Materiale de descărcat</h3>
                        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                            {areFisa && (
                                <ButonDescarcaExercitii
                                    lessonId={lessonId}
                                    titlu={titlu}
                                    pdfManualUrl={lesson.pdfManualUrl as string}
                                    variant="outline"
                                    className="min-h-12 w-full sm:w-auto"
                                    eticheta="Descarcă fișa de exerciții"
                                    cere={(id) => collabApi.exercitiiPdf(token, id)}
                                />
                            )}
                            {areRaspunsuri && (
                                <ButonDescarcaRaspunsuri
                                    lessonId={lessonId}
                                    titlu={titlu}
                                    variant="outline"
                                    className="min-h-12 w-full sm:w-auto"
                                    cere={(id) => collabApi.raspunsuriPdf(token, id)}
                                    cuDiacritice
                                />
                            )}
                        </div>
                        {areRaspunsuri && (
                            <p className="text-sm text-pretty text-dark/70">
                                Răspunsurile sunt pentru tine, nu pentru elevi: nu le arăta înainte ca fișele să fie completate.
                            </p>
                        )}
                    </section>
                )}
            </article>

            {/* Bara nu apare pe o activitate expirata: un buton lipit de ecran spre un formular care
                nu poate reusi doar repeta mesajul de sub titlu. `-mx-4`/`px-4` o intind peste
                marginile lui `main`, ca fundalul sa acopere textul care trece pe sub ea. */}
            {!expirat && (
                <div className="sticky bottom-0 -mx-4 mt-6 border-t border-hairline bg-light/95 px-4 pt-3 safe-area-bottom">
                    <Button type="button" className="min-h-12 w-full" onClick={onContinue} data-colab-continua>
                        <Upload aria-hidden /> {cfg.butonTrimite}
                    </Button>
                </div>
            )}
        </>
    );
}
